import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RangeSlider } from "@/components/ui/range-slider";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type NumberRangeValue = {
  min?: number;
  max?: number;
};

export type NumberRangeBounds = {
  min: number | null;
  max: number | null;
};

export type NumberRangeFilterProps = {
  /** Intervalo selecionado. `undefined` em min/max = sem limite. */
  value?: NumberRangeValue;
  onChange: (value: NumberRangeValue) => void;
  /** Limites reais da coluna, vindos de `limits` na resposta do backend. */
  bounds?: NumberRangeBounds | null;
  /** Casas decimais aceitas pela coluna (0 = inteiro). */
  decimals?: number;
  step?: number;
  format?: "number" | "currency";
  startLabel?: string;
  endLabel?: string;
  loading?: boolean;
  className?: string;
  id?: string;
};

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const clamp = (value: number, lower: number, upper: number) =>
  Math.min(Math.max(value, lower), upper);

/** Aceita "1.234,56", "1234.56", "1234" e "R$ 10,00". */
const parseNumber = (raw: string): number | null => {
  const cleaned = raw.replace(/[^\d.,-]/g, "").trim();
  if (!cleaned) return null;

  const hasDot = cleaned.includes(".");
  const hasComma = cleaned.includes(",");

  let normalized = cleaned;
  if (hasDot && hasComma) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = cleaned.replace(",", ".");
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (
  value: number,
  decimals: number,
  format: "number" | "currency"
) => {
  if (format === "currency") {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
};

const toInputText = (value: number | undefined, decimals: number) => {
  if (!isNumber(value)) return "";
  return decimals > 0 ? String(value).replace(".", ",") : String(value);
};

/**
 * Filtro de intervalo numérico reutilizável: input inicial + input final +
 * Range Slider sincronizado. Usado por todas as colunas numéricas da listagem.
 */
export const NumberRangeFilter = ({
  value,
  onChange,
  bounds,
  decimals = 0,
  step,
  format = "number",
  startLabel = "Valor inicial",
  endLabel = "Valor final",
  loading = false,
  className,
  id,
}: NumberRangeFilterProps) => {
  const boundMin = isNumber(bounds?.min) ? (bounds as NumberRangeBounds).min! : null;
  const boundMax = isNumber(bounds?.max) ? (bounds as NumberRangeBounds).max! : null;
  const hasBounds = boundMin !== null && boundMax !== null;
  const sliderEnabled = hasBounds && (boundMax as number) > (boundMin as number);

  const resolvedStep = step ?? (decimals > 0 ? Number(`0.${"0".repeat(decimals - 1)}1`) : 1);

  const selectedMin = isNumber(value?.min) ? value!.min! : undefined;
  const selectedMax = isNumber(value?.max) ? value!.max! : undefined;

  const [startText, setStartText] = React.useState(() => toInputText(selectedMin, decimals));
  const [endText, setEndText] = React.useState(() => toInputText(selectedMax, decimals));
  const [error, setError] = React.useState<string | null>(null);
  const editingRef = React.useRef(false);

  // Mantém os inputs sincronizados quando o valor muda de fora (slider, limpar filtro...)
  React.useEffect(() => {
    if (editingRef.current) return;
    setStartText(toInputText(selectedMin, decimals));
    setEndText(toInputText(selectedMax, decimals));
  }, [selectedMin, selectedMax, decimals]);

  const round = React.useCallback(
    (raw: number) => Number(raw.toFixed(decimals)),
    [decimals]
  );

  const commit = React.useCallback(
    (rawMin: number | undefined, rawMax: number | undefined) => {
      let nextMin = isNumber(rawMin) ? round(rawMin) : undefined;
      let nextMax = isNumber(rawMax) ? round(rawMax) : undefined;

      if (hasBounds) {
        if (isNumber(nextMin)) nextMin = round(clamp(nextMin, boundMin!, boundMax!));
        if (isNumber(nextMax)) nextMax = round(clamp(nextMax, boundMin!, boundMax!));
      }

      // valor inicial nunca pode ser maior que o final
      if (isNumber(nextMin) && isNumber(nextMax) && nextMin > nextMax) {
        [nextMin, nextMax] = [nextMax, nextMin];
      }

      // intervalo igual aos limites reais => sem filtro (mantém a UI limpa)
      if (
        hasBounds &&
        isNumber(nextMin) &&
        isNumber(nextMax) &&
        nextMin <= boundMin! &&
        nextMax >= boundMax!
      ) {
        nextMin = undefined;
        nextMax = undefined;
      }

      setStartText(toInputText(nextMin, decimals));
      setEndText(toInputText(nextMax, decimals));
      setError(null);

      if (nextMin === selectedMin && nextMax === selectedMax) return;

      onChange({
        ...(isNumber(nextMin) ? { min: nextMin } : {}),
        ...(isNumber(nextMax) ? { max: nextMax } : {}),
      });
    },
    [boundMin, boundMax, hasBounds, round, decimals, onChange, selectedMin, selectedMax]
  );

  const commitFromInputs = React.useCallback(
    (nextStartText: string, nextEndText: string) => {
      const trimmedStart = nextStartText.trim();
      const trimmedEnd = nextEndText.trim();

      const parsedStart = trimmedStart ? parseNumber(trimmedStart) : null;
      const parsedEnd = trimmedEnd ? parseNumber(trimmedEnd) : null;

      if ((trimmedStart && parsedStart === null) || (trimmedEnd && parsedEnd === null)) {
        setError("Informe apenas números.");
        return;
      }

      commit(parsedStart ?? undefined, parsedEnd ?? undefined);
    },
    [commit]
  );

  // Valor "ao vivo" enquanto o thumb está sendo arrastado.
  const [dragValue, setDragValue] = React.useState<[number, number] | null>(null);

  const sliderValue: [number, number] = dragValue ?? [
    isNumber(selectedMin) ? selectedMin : boundMin ?? 0,
    isNumber(selectedMax) ? selectedMax : boundMax ?? 0,
  ];

  const handleSliderChange = (next: [number, number]) => {
    editingRef.current = false;
    setDragValue(next);
    setStartText(toInputText(next[0], decimals));
    setEndText(toInputText(next[1], decimals));
  };

  const handleSliderCommit = (next: [number, number]) => {
    setDragValue(null);
    commit(next[0], next[1]);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    editingRef.current = false;
    commitFromInputs(startText, endText);
  };

  // Enquanto os limites não chegam não mostramos nenhum valor: exibir 0-100 ou
  // qualquer palpite seria um limite artificial. Skeleton padrão do projeto.
  if (loading) {
    return (
      <div className={cn("space-y-3", className)} aria-busy>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-8 w-full" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
        <div className="space-y-1 pt-1">
          <Skeleton className="h-1.5 w-full" />
          <div className="flex justify-between">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-12" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor={id ? `${id}-start` : undefined} className="text-xs font-normal">
            {startLabel}
          </Label>
          <Input
            id={id ? `${id}-start` : undefined}
            inputMode="decimal"
            className="h-8"
            disabled={!hasBounds}
            placeholder={boundMin !== null ? formatNumber(boundMin, decimals, format) : "Mínimo"}
            value={startText}
            onFocus={() => (editingRef.current = true)}
            onChange={(event) => setStartText(event.target.value)}
            onBlur={() => {
              editingRef.current = false;
              commitFromInputs(startText, endText);
            }}
            onKeyDown={handleInputKeyDown}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={id ? `${id}-end` : undefined} className="text-xs font-normal">
            {endLabel}
          </Label>
          <Input
            id={id ? `${id}-end` : undefined}
            inputMode="decimal"
            className="h-8"
            disabled={!hasBounds}
            placeholder={boundMax !== null ? formatNumber(boundMax, decimals, format) : "Máximo"}
            value={endText}
            onFocus={() => (editingRef.current = true)}
            onChange={(event) => setEndText(event.target.value)}
            onBlur={() => {
              editingRef.current = false;
              commitFromInputs(startText, endText);
            }}
            onKeyDown={handleInputKeyDown}
          />
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {sliderEnabled && (
        <div className="space-y-1 pt-1">
          <RangeSlider
            min={boundMin as number}
            max={boundMax as number}
            step={resolvedStep}
            value={sliderValue}
            onChange={handleSliderChange}
            onCommit={handleSliderCommit}
            ariaLabelMin={startLabel}
            ariaLabelMax={endLabel}
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>{formatNumber(boundMin as number, decimals, format)}</span>
            <span>{formatNumber(boundMax as number, decimals, format)}</span>
          </div>
        </div>
      )}

      {hasBounds && !sliderEnabled && (
        <p className="text-xs text-muted-foreground">
          Todos os registros possuem o mesmo valor (
          {formatNumber(boundMin as number, decimals, format)}).
        </p>
      )}

      {!hasBounds && (
        <p className="text-xs text-muted-foreground">
          Sem valores disponíveis para montar o intervalo.
        </p>
      )}
    </div>
  );
};
